import moment from 'moment';
import ItinerariesApi from '../services/ItinerariesApi';
import {
  filenameFromDisposition,
  readBlobError,
} from '@/shared/utils/downloadBlob';
import {
  mapItineraryFromApi,
  mapDraftFromApi,
  mapPessoasFromLookup,
  mapAddedPessoaIds,
  mapHistoryFromApi,
  mapSupplierOptionsFromApi,
} from '../utils/itineraryMapper';
import { ITINERARY_STATUS } from '../constants/itineraryStatus';

const SUPPLIER_SEARCH_MIN_LENGTH = 3;

const STATUS_PARAM_MAP = {
  todos: undefined,
  ativos: String(ITINERARY_STATUS.ATIVO),
  inativos: String(ITINERARY_STATUS.INATIVO),
};

function buildSearchParams(search) {
  if (!search) {
    return {};
  }

  const value = String(search).trim();
  return /^rt-?\d+$/i.test(value) ? { cd: value.toUpperCase() } : { descr: value };
}

function fetchCounts(searchParams) {
  return Promise.all([
    ItinerariesApi.fetchAll({ ...searchParams, page: 1, per_page: 1 }),
    ItinerariesApi.fetchAll({
      ...searchParams,
      status: STATUS_PARAM_MAP.ativos,
      page: 1,
      per_page: 1,
    }),
    ItinerariesApi.fetchAll({
      ...searchParams,
      status: STATUS_PARAM_MAP.inativos,
      page: 1,
      per_page: 1,
    }),
  ]).then(([todos, ativos, inativos]) => ({
    todos: todos.data.meta.total,
    ativos: ativos.data.meta.total,
    inativos: inativos.data.meta.total,
  }));
}

let requestSeq = 0;

export async function fetchRoteiros({ commit, state }, overrides = {}) {
  const seq = ++requestSeq;
  commit('SET_LOADING', true);
  commit('SET_ERROR', null);

  const filters = {
    search: overrides.search !== undefined ? overrides.search : state.filters.search,
    status: overrides.status !== undefined ? overrides.status : state.filters.status,
    sort: overrides.sort !== undefined ? overrides.sort : state.filters.sort,
    personIds:
      overrides.personIds !== undefined ? overrides.personIds : state.filters.personIds,
  };
  commit('SET_FILTERS', filters);

  const page = overrides.page || 1;
  const perPage = overrides.perPage || state.pagination.perPage;
  const searchParams = buildSearchParams(filters.search);
  if (filters.personIds && filters.personIds.length) {
    searchParams.person_ids = filters.personIds;
  }
  const listParams = {
    ...searchParams,
    status: STATUS_PARAM_MAP[filters.status],
    per_page: perPage,
  };
  if (filters.sort) {
    listParams.sort = filters.sort;
  }
  const countsKey =
    filters.search + '|' + (filters.personIds || []).slice().sort().join(',');
  const needCounts =
    overrides.refreshCounts === true || countsKey !== state.countsSearch;

  try {
    const listPromise = ItinerariesApi.fetchAll({ ...listParams, page });
    const countsPromise = needCounts ? fetchCounts(searchParams) : null;

    const listResponse = await listPromise;
    if (seq !== requestSeq) return;

    const meta = listResponse.data.meta;
    commit('SET_ITINERARIES', {
      items: listResponse.data.data.map(mapItineraryFromApi),
      pagination: {
        current: meta.current_page,
        perPage: meta.per_page,
        total: meta.total,
        lastPage: meta.last_page,
      },
    });

    if (countsPromise) {
      const counts = await countsPromise;
      if (seq !== requestSeq) return;
      commit('SET_COUNTS', { search: countsKey, ...counts });
    }
  } catch (error) {
    if (seq !== requestSeq) return;
    commit('SET_ERROR', error);
    throw error;
  } finally {
    if (seq === requestSeq) commit('SET_LOADING', false);
  }
}

export async function createDraft() {
  const response = await ItinerariesApi.createDraft();
  return mapDraftFromApi(response.data.data);
}

export async function updateItineraryDetails(context, { itineraryId, descr, obs }) {
  await ItinerariesApi.updateDetails(itineraryId, { descr, obs });
}

export async function searchPessoas(context, { itineraryId, search }) {
  const response = await ItinerariesApi.searchPessoas(itineraryId, search);
  return mapPessoasFromLookup(response.data.data);
}

export async function searchSuppliers(context, { search }) {
  const term = String(search || '').trim();
  if (term.length < SUPPLIER_SEARCH_MIN_LENGTH) {
    return [];
  }
  const response = await ItinerariesApi.searchSuppliers(term);
  return mapSupplierOptionsFromApi(response.data);
}

export async function fetchItineraryHistory(context, { itineraryId, page, perPage, filters }) {
  const params = { page: page || 1, per_page: perPage || 10 };
  const f = filters || {};
  if (f.search) params.search = f.search;
  if (f.events && f.events.length) params.event = f.events.join(',');
  if (f.userId) params.user_id = f.userId;
  if (f.dateFrom) params.date_from = f.dateFrom;
  if (f.dateTo) params.date_to = f.dateTo;

  const response = await ItinerariesApi.fetchHistory(itineraryId, params);
  return {
    entries: mapHistoryFromApi(response.data.data),
    meta: response.data.meta,
  };
}

export async function syncItineraryPessoas(context, { itineraryId, personIds }) {
  const response = await ItinerariesApi.syncPessoas(itineraryId, personIds);
  return mapAddedPessoaIds(response.data.itinerary_pessoa_juridicas);
}

export async function submitItinerary(context, { itineraryId }) {
  await ItinerariesApi.submit(itineraryId);
}

export async function deleteItinerary(context, { itineraryId }) {
  await ItinerariesApi.remove(itineraryId);
}

export async function updateItinerariesStatus({ dispatch, state }, { itineraryIds, status }) {
  await ItinerariesApi.updateStatus(itineraryIds, status);
  await dispatch('fetchRoteiros', {
    page: state.pagination.current,
    refreshCounts: true,
  });
}

export async function importItineraries(context, { file }) {
  const response = await ItinerariesApi.importSpreadsheet(file);
  return response.data;
}

// Não exportar: todo export deste arquivo vira action.
function defaultExportFilename() {
  return 'roteiros_' + moment().format('YYYYMMDDHHmmss') + '.xlsx';
}

export async function exportItineraries(context, { itineraryIds }) {
  try {
    const response = await ItinerariesApi.exportSpreadsheet(itineraryIds);
    const disposition =
      response.headers && response.headers['content-disposition'];

    return {
      blob: response.data,
      filename: filenameFromDisposition(disposition, defaultExportFilename()),
    };
  } catch (error) {
    throw await readBlobError(error);
  }
}

export function resetState({ commit }) {
  commit('RESET_STATE');
}
